import { useEffect, useState } from 'react'
import cx from 'classnames'

import { pluralize } from 'utils'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import Styles from 'components/ShareSheet/ShareSheet.module.scss'
import { useGroupMembers } from 'hooks/queries/groups/useGroupMembers'
import { ReactComponent as RightChevron } from 'assets/icons/chevronRight12.svg'
import { ReactComponent as DownChevron } from 'assets/icons/chevronDown12.svg'
import { AccessorAvatar } from 'components/ShareSheet/Access/AccessorAvatar'
import { buildGroupValue } from './utils'
import { Value } from 'components/ShareSheet/RecipientSelector/Types'
import { GroupInfo } from './Types'
import { RecipientsSelectorValueType } from 'types'

export const DefaultGroupRow = ({
  group,
  childRows,
  dispatch,
  index,
  nested,
  activeIndex,
  addGroup,
  refocus,
}: {
  group: GroupInfo
  childRows?: GroupInfo[]
  nested?: boolean
  dispatch: (value: any) => void
  index?: number
  activeIndex?: number
  addGroup?: (group: Value) => void
  refocus: () => void
}) => {
  const { isMobile } = useResponsiveDesign()
  const { avatar_url, name, type, member_count } = group
  const highlighted = index !== undefined && index === activeIndex
  const [expanded, setExpanded] = useState(false)
  const [rowsToShow, setRowsToShow] = useState(childRows || [])
  const currentWorkspace = useCurrentWorkspace()
  const onMouseEnter = () => dispatch({ type: 'updateHighlightedSuggestionIndex', index })
  const onMouseLeave = () => {
    dispatch({ type: 'updateHighlightedSuggestionIndex', index: -1 })
  }
  const { data } = useGroupMembers({ groupId: group.id, enabled: expanded && group.type === 'Channel' })

  useEffect(() => {
    if (!data?.length) return

    const rows = data.map((membership) => {
      const {
        member: { id, avatar_url, display_name },
      } = membership
      return { id: id, avatar_url: avatar_url || '', name: display_name, type: RecipientsSelectorValueType.User }
    })

    setRowsToShow(rows)
  }, [data])

  const handlePress = () => {
    if (!!addGroup) {
      dispatch({ type: 'updateQuery', query: '' })
      addGroup(buildGroupValue(group.type, group, currentWorkspace))
    }
  }

  return (
    <>
      <div
        key={`default-group-${type}-${group.id}-${index}`}
        className={cx(Styles.expandedRow, { [Styles.highlighted]: highlighted, [Styles.withHover]: !!member_count })}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onClick={handlePress}
      >
        <div className={Styles.expandedRowContents}>
          <AccessorAvatar type={type} avatarUrl={avatar_url} name={name} size={24} />
          <span
            className={cx(Styles.name, {
              [Styles.everyoneGroup]: type === RecipientsSelectorValueType.EveryoneAtWorkspace,
              [Styles.nested]: !!nested,
            })}
          >
            {name}
          </span>
          {!!member_count && (
            <div
              className={Styles.groupItems}
              onClick={(e) => {
                refocus()
                e.stopPropagation()
                setExpanded((prev) => !prev)
              }}
            >
              {expanded ? <DownChevron /> : <RightChevron />}
            </div>
          )}
        </div>
        {!isMobile && !!member_count && (
          <div className={Styles.memberCount}>
            {member_count} {pluralize('member', member_count || 1)}
          </div>
        )}
      </div>
      <div className={cx(Styles.expandedContents, { [Styles.showing]: expanded })}>
        {rowsToShow.map((row, index) => (
          <DefaultGroupRow
            key={`expanded-${row.type}-${row.id}-${index}`}
            dispatch={dispatch}
            group={row}
            nested
            refocus={refocus}
          />
        ))}
      </div>
    </>
  )
}
